import React, { useEffect, useState } from "react";
import { CheckCircle2, Clock } from "lucide-react";

/**
 * Session 30.10 — Pending orders for a table, with a LIVE "pending for"
 * timer per order so a manager can spot tickets that have been waiting
 * too long. Pending = placed but not yet delivered (status pending or
 * confirmed). Re-renders every second while anything is pending.
 */
function fmtElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m ${String(sec).padStart(2, "0")}s`;
  return `${m} min ${String(sec).padStart(2, "0")} sec`;
}

function toneFor(ms, thresholdMin = 15) {
  const min = ms / 60000;
  if (min >= thresholdMin) return "border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-800/60 dark:bg-rose-950/40 dark:text-rose-100";
  if (min >= thresholdMin / 2) return "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-100";
  return "border-border bg-secondary/50 text-foreground";
}

export function PendingOrdersPanel({ orders, testIdPrefix = "pending-orders", thresholdMin = 15 }) {
  const pending = (orders || []).filter((o) => o && ["pending", "confirmed"].includes(String(o.status || "").toLowerCase()));
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (pending.length === 0) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [pending.length]);

  if (pending.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs font-medium text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-950/30 dark:text-emerald-200" data-testid={`${testIdPrefix}-none`}>
        <CheckCircle2 className="h-4 w-4" />
        No pending orders — everything on this table has been delivered.
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-list`}>
      {pending.map((o, idx) => {
        const items = (o.items || []).filter((it) => !it.voided);
        const count = items.reduce((n, it) => n + Number(it.quantity || 0), 0);
        const started = new Date(o.created_at || o.placed_at || o.updated_at || Date.now()).getTime();
        const elapsed = now - started;
        const label = o.order_number ? `Order ${o.order_number}` : `Pending order ${idx + 1}`;
        return (
          <div key={o.id || idx} className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border px-3 py-2 ${toneFor(elapsed, thresholdMin)}`} data-testid={`${testIdPrefix}-row-${o.id || idx}`}>
            <Clock className="h-4 w-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">
                {label} — {count} item{count === 1 ? "" : "s"}
                <span className="ml-2 rounded-full border border-current/30 px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-wide opacity-80">{String(o.status)}</span>
              </p>
              <p className="truncate text-[11px] opacity-80">{items.map((it) => `${it.quantity}× ${it.name}`).join(", ")}</p>
            </div>
            <p className="shrink-0 text-sm font-bold tabular-nums" data-testid={`${testIdPrefix}-elapsed-${o.id || idx}`}>
              Pending for: {fmtElapsed(elapsed)}
            </p>
          </div>
        );
      })}
    </div>
  );
}
